import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges, TemplateRef } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { RtTooltip } from '@recruitee/user-interface';

import { LIKE, ReactionData } from '../../types/reaction-data.types';
import { AddReactionButtonComponent } from '../add-reaction-button/add-reaction-button.component';
import { LikeButtonComponent } from '../like-button/like-button.component';
import { ReactionButtonComponent } from '../reaction-button/reaction-button.component';

type ReactionDataWithTooltip = ReactionData & { tooltip: string };

@Component({
  selector: 'rt-reactions',
  templateUrl: './reactions.component.html',
  styleUrls: ['./reactions.component.less'],
  standalone: true,
  imports: [
    ReactionButtonComponent,
    RtTooltip,
    LikeButtonComponent,
    AddReactionButtonComponent,
    NgTemplateOutlet,
  ],
})
export class ReactionsComponent {
  @Input() public reactionsData: ReactionData[] = [];
  @Input() public projectedContentTpl: TemplateRef<any>;
  @Output() public addExistingReaction: EventEmitter<string> = new EventEmitter();
  @Output() public addNewReaction: EventEmitter<string> = new EventEmitter();
  @Output() public removeReaction: EventEmitter<string> = new EventEmitter();

  public reactionsDataWithTooltip: ReactionDataWithTooltip[] = [];

  public showLikeButton: boolean;

  constructor(private i18n: I18nService) {}

  public ngOnInit(): void {
    this.showLikeButton = this.shouldShowLikeButton();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reactionsData?.currentValue) {
      this.reactionsDataWithTooltip = this.addTooltipTexts(this.reactionsData);
      this.showLikeButton = this.shouldShowLikeButton();
    }
  }

  public trackByReaction(index: number, item: ReactionDataWithTooltip): string {
    return item.reaction;
  }

  public toggle(reaction: string): void {
    const existingReaction = this.reactionsData.find(data => data.reaction === reaction);
    if (existingReaction) {
      const { currentUserReacted } = existingReaction;
      if (currentUserReacted) this.removeReaction.emit(reaction);
      else this.addExistingReaction.emit(reaction);
    } else {
      this.addNewReaction.emit(reaction);
    }
  }

  public toggleLike() {
    this.toggle(LIKE);
  }

  private shouldShowLikeButton(): boolean {
    return !this.reactionsData.map(reaction => reaction.reaction).includes(LIKE);
  }

  private addTooltipTexts(reactionsData: ReactionData[]): ReactionDataWithTooltip[] {
    return reactionsData.map(item => {
      let tooltip;
      const userNames = item.otherUsersWhoReacted.join(', ');
      const reactionName = `:${item.reactionShortName}:`;
      const multipleOthers = item.otherUsersWhoReacted.length > 1;
      if (item.currentUserReacted) {
        if (!!item.otherUsersWhoReacted.length) {
          tooltip = this.i18n.translate(
            multipleOthers
              ? 'app.reactions.tooltip_me_and_others_plural'
              : 'app.reactions.tooltip_me_and_others',
            {
              userNames,
              reactionName,
            },
          );
        } else {
          tooltip = this.i18n.translate('app.reactions.tooltip_only_me', { reactionName });
        }
      } else {
        tooltip = this.i18n.translate(
          multipleOthers
            ? 'app.reactions.tooltip_only_others_plural'
            : 'app.reactions.tooltip_only_others',
          {
            userNames,
            reactionName,
          },
        );
      }

      return {
        ...item,
        tooltip,
      };
    });
  }
}
